import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { useTableSort } from '../../lib/tableSort.js';
import { RoutineTasksAPI } from '../../lib/api';
import { buildPath, ROUTES, ROUTINE_FREQUENCIES, ROUTINE_STATUS } from '../../config';

/**
 * Stream a Blob the browser received from axios down to disk under the
 * filename advertised in the response's ``Content-Disposition``
 * header.  Falls back to ``fallbackName`` when the header isn't
 * present (e.g. when the browser strips it from a CORS response).
 */
function downloadBlobResponse(response, fallbackName) {
  const blob = new Blob([response.data], {
    type:
      response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  let filename = fallbackName;
  const cd = response.headers['content-disposition'] || '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Routine-task list view.
 *
 * Routine tasks are *templates*. The daily cron
 * (``task_management.cron.assign_task_to_user`` →
 * ``scripts.routine_task_cron.run``) expands them into assigned Task
 * rows on the configured cadence. This page is the schedule manager.
 * "Run now" is a per-row bypass of that cron, not the automatic path.
 * It mirrors the shape of ``TasksList`` but with routine-specific
 * filters (frequency, status) and the Run now / Edit / Delete actions.
 */
export default function RoutineTasksList() {
  const [search, setSearch] = useState('');
  const [frequency, setFrequency] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ``runningId`` flips a per-row spinner on while the optional
  // "run-now" call is in flight.  Lives at the page level (not the
  // table row) so we can disable every row's button to avoid double
  // submits.
  const [runningId, setRunningId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // ``exporting`` disables the Export button while the .xlsx blob is
  // being assembled server-side so the user can't fire a second
  // download by accident.
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = { ordering: '-created_on' };
    if (search) p.search = search;
    if (frequency) p.frequency = frequency;
    if (statusFilter) p.status = statusFilter;
    return p;
  }, [search, frequency, statusFilter]);

  const reload = () => {
    setLoading(true);
    return RoutineTasksAPI.list(params)
      .then((data) => setItems(data.results || data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    RoutineTasksAPI.list(params)
      .then((data) => {
        if (cancelled) return;
        setItems(data.results || data);
      })
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const { sortedRows, sort, toggleSort } = useTableSort(items, {
    getValue: (row, key) => {
      if (key === 'title') return row.title;
      if (key === 'schedule') return row.schedule_summary || row.cron_repeat_time || row.task_frequency;
      if (key === 'active') return `${row.start_date || ''} ${row.end_date || ''}`;
      if (key === 'status') return row.status;
      return row[key];
    },
  });

  const onDelete = (row) => {
    // Soft-confirm — same UX as the rest of the SPA which uses the
    // browser's built-in confirm dialog for destructive actions.
    const ok = window.confirm(
      `Delete the routine "${row.title}"? Existing Task rows it has\n` +
      `already produced will be kept; only the schedule disappears.`
    );
    if (!ok) return;
    RoutineTasksAPI.remove(row.id)
      .then(() => {
        setFeedback(`Deleted "${row.title}".`);
        reload();
      })
      .catch((e) => setError(e));
  };

  const onRunNow = (row) => {
    setRunningId(row.id);
    setFeedback(null);
    RoutineTasksAPI.runNow(row.id)
      .then(() => {
        setFeedback(
          `Run now (bypass) finished — any tasks due today for "${row.title}" have been assigned. The daily cron still owns automatic assignment.`,
        );
      })
      .catch((e) => setError(e))
      .finally(() => setRunningId(null));
  };

  // ---- Excel export -------------------------------------------------------
  // The server already filters the queryset to "this user's routine
  // tasks", so we don't need to send any params here — the export
  // mirrors what the user can see on this page.  We DO pass through
  // the active filters though, so an export taken while filtered
  // produces a file matching the visible rows.
  const onExport = () => {
    setExporting(true);
    setError(null);
    setFeedback(null);
    RoutineTasksAPI.exportXlsx(params)
      .then((response) => {
        downloadBlobResponse(response, 'routine_tasks.xlsx');
        setFeedback(
          'Export complete — your routine tasks have been downloaded as Excel.',
        );
      })
      .catch((e) => setError(e))
      .finally(() => setExporting(false));
  };

  return (
    <>
      <PageHeader
        title="Routine Tasks"
        subtitle="Templates the daily cron turns into assigned tasks on a daily, weekly, monthly or yearly schedule. Run now is a manual bypass if that cron has not run yet."
        actions={
          <>
            {/* Excel round-trip actions.  ``Export`` streams the
                current view's rows as .xlsx; ``Import`` opens the
                instructions + upload page (which can also link back
                here on success). */}
            <button
              type="button"
              className="ty-btn ty-btn--ghost"
              onClick={onExport}
              disabled={exporting}
              title="Download these routine tasks as an Excel (.xlsx) file"
            >
              {exporting ? 'Exporting…' : '⬇ Export to Excel'}
            </button>{' '}
            <Link
              className="ty-btn ty-btn--ghost"
              to={ROUTES.ROUTINE_TASK_IMPORT}
              title="Bulk-create routine tasks from an Excel (.xlsx) file"
            >
              ⬆ Import from Excel
            </Link>{' '}
            <Link className="ty-btn" to={buildPath.routineTaskNew()}>
              + New routine
            </Link>
          </>
        }
      />

      <div className="ty-filters">
        <select
          className="ty-select"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
        >
          <option value="">All frequencies</option>
          {ROUTINE_FREQUENCIES.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <select
          className="ty-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {ROUTINE_STATUS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <input
          className="ty-input"
          placeholder="Search by title or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {feedback && (
        <div className="ty-alert ty-alert--info" role="status">{feedback}</div>
      )}
      <ErrorBox error={error} />

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="ty-empty">
          You don&apos;t have any routine tasks yet.{' '}
          <Link to={buildPath.routineTaskNew()}>Create one</Link> to start
          spawning tasks automatically.
        </div>
      ) : (
        <div className="ty-table-wrap">
          <table className="ty-table">
            <thead>
              <tr>
                <SortableTh label="Title" sortKey="title" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Schedule" sortKey="schedule" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Active window" sortKey="active" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Status" sortKey="status" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.id}>
                  <td data-label="Title">
                    <Link to={buildPath.routineTaskEdit(row.id)}>{row.title}</Link>
                    {row.description ? (
                      <div className="ty-cell-sub">{row.description}</div>
                    ) : null}
                  </td>
                  <td data-label="Schedule">
                    {row.schedule_summary || row.cron_repeat_time || row.task_frequency}
                  </td>
                  <td data-label="Active">
                    {row.start_date || '—'} → {row.end_date || '—'}
                  </td>
                  <td data-label="Status">
                    {row.status || 'not_started'}
                  </td>
                  <td data-label="">
                    <Link
                      className="ty-btn ty-btn--ghost ty-btn--sm"
                      to={buildPath.routineTaskEdit(row.id)}
                    >
                      Edit
                    </Link>{' '}
                    <button
                      type="button"
                      className="ty-btn ty-btn--ghost ty-btn--sm"
                      disabled={runningId === row.id}
                      onClick={() => onRunNow(row)}
                      title="Bypass: assign today's task now if the daily cron has not run"
                    >
                      {runningId === row.id ? 'Running…' : 'Run now'}
                    </button>{' '}
                    <button
                      type="button"
                      className="ty-btn ty-btn--ghost ty-btn--sm ty-btn--danger"
                      onClick={() => onDelete(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
